import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/utils";

interface EmptyStateProps {
  /** Optional Lucide icon shown in a subtle rounded badge above the title. */
  icon?: LucideIcon;
  /**
   * Optional uppercase micro-label (eyebrow) shown above the headline — the
   * Atlas turn-1d pattern. Pass an already-translated string; the package stays
   * i18n-free.
   */
  label?: string;
  /** Headline. Pass an already-translated string. */
  title: string;
  /** Optional muted supporting copy. */
  description?: string;
  /** Optional call-to-action (button/link). */
  action?: ReactNode;
  /**
   * Heading element for the title. Defaults to `h3`. Set to the level that keeps
   * the surrounding document heading order valid (e.g. `h2` directly under the
   * page `h1` when there is no intermediate heading).
   */
  headingLevel?: "h2" | "h3" | "h4";
  className?: string;
}

/**
 * Centered empty / placeholder state. Use for "no data yet", "nothing here",
 * and zero-result surfaces instead of leaving an area blank.
 *
 * Presentational only — pass already-translated strings.
 */
export function EmptyState({
  icon: Icon,
  label,
  title,
  description,
  action,
  headingLevel: Heading = "h3",
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center overflow-hidden rounded-card border px-6 py-16 text-center",
        className,
      )}
    >
      {/* Decorative offset rings (accent + deep-teal chart-4) at low alpha —
          C2 hairlines-over-shadows: borders, never box-shadows. */}
      <span
        aria-hidden="true"
        className="border-primary/20 pointer-events-none absolute -top-8 -left-8 size-28 rounded-full border"
      />
      <span
        aria-hidden="true"
        className="border-chart-4/15 pointer-events-none absolute -right-10 -bottom-10 size-32 rounded-full border"
      />
      {label && (
        <span className="text-muted-foreground mb-2 text-[10px] font-bold tracking-[0.08em] uppercase">
          {label}
        </span>
      )}
      {Icon && (
        <div
          className="bg-muted text-muted-foreground rounded-count mb-4 flex size-12 items-center justify-center"
          aria-hidden="true"
        >
          <Icon className="size-6" />
        </div>
      )}
      <Heading className="text-foreground text-[13.5px] font-bold">
        {title}
      </Heading>
      {description && (
        <p className="text-muted-foreground mt-1 max-w-sm text-[13px] text-pretty">
          {description}
        </p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export type { EmptyStateProps };
